import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TypedUseSelectorHook } from "../store";
import { setup, isSupported, SDKState, SDKButtonInterface } from "@loomhq/loom-sdk";
import SnowGlobe from "./SnowGlobe";
import Game from "./Game";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth, useFirestore, useFirestoreDocData } from "reactfire";
import { doc, setDoc } from "firebase/firestore";
import IGame from "../interfaces/Game";
import Results from "./Results";
import { QuestionRecord } from "../interfaces/GameUser";
import { Button } from "react-bootstrap";

export interface RecordProps {}

interface RouteParams {
	gameId: string;
}

const useTypedSelector: TypedUseSelectorHook = useSelector;

const API_KEY = "910aaac1-5fe8-406d-827f-0943e10d5946";
const BUTTON_ID = "loom-sdk-button";

const Record: React.FC<RecordProps> = ({}) => {
	const [videoHTML, setVideoHTML] = useState("");
	const [gameStarted, setGameStarted] = useState(false);
	const [showGame, setShowGame] = useState(false);
	const [showResults, setShowResults] = useState(false);
	const [loomAdded, setLoomAdded] = useState(false);
	const [loomUrl, setLoomUrl] = useState<string>();
	const [sdkState, setSdkState] = useState<SDKState>();
	const [loomButton, setLoomButton] = useState<SDKButtonInterface>();

	const params = useParams();
	const navigate = useNavigate();

	const firestore = useFirestore();
	const auth = useAuth();
	const gameRef = doc(firestore, "games", params.gameId ?? "");
	const gameData = useFirestoreDocData(gameRef).data as IGame;

	const userAdded = auth.currentUser?.uid && gameData?.status?.[auth.currentUser.uid];

	const setVideo = (loomUrl: string) => {
		if (auth.currentUser?.uid && gameData.status[auth.currentUser.uid].loomUrl !== loomUrl) {
			gameData.status[auth.currentUser.uid].loomUrl = loomUrl;

			setDoc(gameRef, gameData).then(() => {
				setTimeout(() => {
					setShowResults(true);
				}, 1000);
			});
		}
	};
	const addUser = (gData: IGame) => {
		if (auth.currentUser?.uid) {
			const questions = JSON.parse(JSON.stringify(Object.values(gameData.status)[0].questions)) as QuestionRecord[];
			questions.forEach((question) => {
				delete question.answer;
			});
			gameData.status[auth.currentUser.uid] = {
				completed: false,
				name: auth.currentUser.displayName,
				image: auth.currentUser.photoURL,
				questions
			};
			gameData.users.push(auth.currentUser.uid);

			setDoc(gameRef, gameData).then(() => {});
		}
	};

	const resetAnswers = (gData: IGame) => {
		if (auth.currentUser?.uid) {
			const questions = Object.values(gameData.status)[0].questions.concat([]);
			questions.forEach((question) => {
				delete question.answer;
			});
			gameData.status[auth.currentUser.uid] = {
				completed: false,
				name: auth.currentUser.displayName,
				image: auth.currentUser.photoURL,
				questions
			};

			setDoc(gameRef, gameData).then(() => {});
		}
	};

	useEffect(() => {
		if (loomUrl) {
			setVideo(loomUrl);
		}
	}, [loomUrl]);

	useEffect(() => {
		async function setupLoom() {
			const { supported, error } = await isSupported();

			if (!supported) {
				console.warn(`Error setting up Loom: ${error}`);
				return;
			}

			const button = document.getElementById(BUTTON_ID);

			if (!button) {
				return;
			}

			const { configureButton } = await setup({
				apiKey: API_KEY,
				config: {
					bubbleResizable: false,
					insertButtonText: "Save And See Results!"
				}
			});

			const sdkButton = configureButton({ element: button });

			sdkButton.on("lifecycle-update", (lifecycle) => {
				centerBubble();
				setSdkState(lifecycle);
			});

			sdkButton.openPreRecordPanel();

			const centerBubble = () => {
				sdkButton.moveBubble({
					x: window.innerWidth / 2 - 112,
					y: -1 * (window.innerHeight / 2 - 124)
				});
			};

			sdkButton.on("bubble-drag-end", () => {
				centerBubble();
			});

			sdkButton.on("cancel", () => {
				resetAnswers(gameData);
			});

			window.onresize = () => {
				centerBubble();
			};

			centerBubble();

			sdkButton.on("insert-click", async (video) => {
				setLoomUrl(video.sharedUrl);
			});

			setLoomButton(sdkButton);
		}
		if (auth.currentUser?.uid && gameData?.status[auth.currentUser.uid]) {
			if (gameData.status[auth.currentUser.uid].loomUrl) {
				setShowResults(true);
			} else if (!loomAdded) {
				setLoomAdded(true);
				setupLoom();
			}
		}
	}, [auth.currentUser?.uid, gameData?.status]);

	useEffect(() => {
		if (sdkState === "pre-recording") {
			setGameStarted(true);
		} else if (sdkState === "active-recording") {
			setShowGame(true);
		} else if (sdkState === "post-recording") {
			setShowGame(false);
		} else if (sdkState === "closed") {
			setGameStarted(false);
			setShowGame(false);
		}
	}, [sdkState]);

	return (
		<>
			<Button
				className="back-home"
				onClick={() => {
					navigate("/");
				}}
			>
				<div className="deco">Back Home</div>
			</Button>
			<div className="snowglobe-wrapper">
				<SnowGlobe />
			</div>
			{showResults && <Results gameData={gameData} auth={auth} />}
			{showGame && gameData && params.gameId && (
				<Game gameData={gameData} auth={auth} documentId={params.gameId} loomButton={loomButton} />
			)}
			<button id={BUTTON_ID} className={`start-game-button ${gameStarted || showResults || !userAdded ? "hide" : ""}`}>
				<div className="deco">Start Game</div>
			</button>

			<button
				className={`start-game-button ${userAdded ? "hide" : ""}`}
				onClick={() => {
					addUser(gameData);
				}}
			>
				<div className="deco">Join Game</div>
			</button>

			<div dangerouslySetInnerHTML={{ __html: videoHTML }}></div>
		</>
	);
};

export default Record;
